import * as React from 'react';
import cn from 'classnames';
import { motion } from 'framer-motion';
import { Dialog, DialogDisclosure, DialogBackdrop, useDialogState, DialogDisclosureProps, DialogStateReturn } from 'reakit';
import { useGaudiLocalization } from '../Provider/Provider';
import { IconButton } from '../IconButton/IconButton';
import { useDraggable } from '../../utils/hooks/useDraggable';
import './Modal.css';

export type ModalTexts = {
    /**
     * Aria-label for close icon button in modal.
     * To read more about how to provide the text, see [Provider](component:provider) component
     */
    close: string;
    drag: string;
};

export type ModalDrawerControllerProps = {
    /** Whether the drawer is visible or not */
    visible: boolean;
    /** Toggles drawer's visibility */
    toggle: () => void;
    /** Shows the drawer */
    show: () => void;
    /** Hides the drawer */
    hide: () => void;
};

export type ModalRenderProps = DialogStateReturn & {
    /** Access drawer's properties in a render function */
    drawer: ModalDrawerControllerProps;
};

export type ModalCloseReason =
    | 'close' // Closed via the close button
    | 'renderprop' // Closed via the provided render prop
    | 'other'; // Unknown reason, probably backdrop

export type ModalSize = 'dialog' | 'xs' | 'sm' | 'md' | 'lg';

export type ModalProps = React.HTMLAttributes<HTMLDivElement> & {
    /**
     * Content of modal can be any valid react node, for e.g. a `div`,
     * or a render function that is given the properties of the dialog and drawer.
     * The modal content though should be composed using modal's components related to each section of the modal:
     * `Modal.Header` - representing the title of the modal within a header element. Read about `ModalHeaderProps` for more details.
     * `Modal.Content` - representing the content of the modal. See `ModalContentProps` for more details.
     * `Modal.Footer` - representing the footer of the modal. See `ModalFooterProps` for more details.
     */
    children: Omit<React.ReactNode, 'Function'> | ((props: ModalRenderProps) => JSX.Element);
    /** Defines the component that controls visibility of the modal */
    disclosure?: JSX.Element;
    /**
     * Aditional content anchored to the right side of the modal's content, that slides and is scrollable.
     * Can be any valid jsx element, for e.g. a `div`,
     * or a render function that is given the properties of the dialog and drawer
     */
    drawer?: Omit<React.ReactNode, 'Function'> | ((props: ModalRenderProps) => JSX.Element);
    /**
     * Set whether the drawer is visible when modal opens.
     * The default value is `false`.
     */
    drawerDefaultVisible?: boolean;
    /**
     * Additional content displayed in the bottom area of the modal.
     * Can be any valid jsx element, for e.g. a `div`,
     * or a render function that is given the properties of the dialog and drawer
     */
    extra?: Omit<React.ReactNode, 'Function'> | ((props: ModalRenderProps) => JSX.Element);
    /** When `true`, user can hide the dialog by pressing `Escape` */
    hideOnEsc?: boolean;
    /** When `true`, user can hide the dialog by clicking outside it */
    hideOnClickOutside?: boolean;
    /** Allows dragging the modal around the screen (window constrained) */
    draggable?: boolean;
    /** Handler called when dialog hides */
    onHide?: (reason: ModalCloseReason) => void;
    /**
     * Shows the close icon button of the dialog.
     * Default value is `true`.
     */
    showCloseButton?: boolean;
    /** Size of the modal. This is the recommended way to set a size for modal component. */
    size?: ModalSize;
    /** Set whether the modal is visible or not. */
    visible?: boolean;
};

const VisibilityWrapper = ({ children, disclosureRef }: any) => {
    React.useEffect(() => {
        return () => {
            if (disclosureRef && disclosureRef.current) {
                disclosureRef.current.focus();
            }
        };
    }, []);

    return children;
};

export const Modal = (props: ModalProps): JSX.Element => {
    const {
        children,
        disclosure,
        showCloseButton = true,
        visible = false,
        drawerDefaultVisible = false,
        onHide,
        drawer,
        extra,
        hideOnClickOutside = false,
        draggable = false,
        size = 'medium',
        ...otherProps
    } = props;

    const defaultDisclosureRef = React.useRef(typeof document !== 'undefined' ? document.body : null);
    const backdropRef = React.useRef<HTMLDivElement | null>(null);
    const disclosureRef = React.useRef(null);
    const modalRef = React.useRef(null);
    const { position, handleProps: dragHandleProps } = useDraggable(modalRef);

    const { texts } = useGaudiLocalization();

    const dialog = useDialogState({ visible });
    const [drawerVisible, setDrawerVisible] = React.useState(drawerDefaultVisible);
    // remember if modal was open before (hint: in the beginning it wasn't!)
    const wasModalVisible = React.useRef(dialog.visible);
    // we want to know how the dialog was closed! 'other' means backdrop, but maybe not...
    const closeReasonRef = React.useRef<ModalCloseReason>('other');

    // detect closing of the modal by comparing visibility between states
    React.useEffect(() => {
        if (wasModalVisible.current && !dialog.visible && onHide) {
            onHide(closeReasonRef.current);
        }

        if (!dialog.visible) {
            closeReasonRef.current = 'other';
            setDrawerVisible(drawerDefaultVisible);
        }

        wasModalVisible.current = dialog.visible;
    }, [wasModalVisible, dialog.visible]);

    function renderPropHide(): void {
        closeReasonRef.current = 'renderprop';
        dialog.hide();
    }

    let ContentWrapper: React.ExoticComponent | string = React.Fragment;
    const wrapperProps: React.HtmlHTMLAttributes<HTMLDivElement> = {};

    if (!!drawer) {
        ContentWrapper = 'div';
        wrapperProps.className = 'yt-modal__drawer-wrapper';
    }

    const renderProps: ModalRenderProps = {
        ...dialog,
        hide: renderPropHide,
        drawer: {
            visible: !!drawer && drawerVisible,
            toggle: () => {
                if (!!drawer) return setDrawerVisible(!drawerVisible);
            },
            show: () => {
                if (!!drawer) return setDrawerVisible(true);
            },
            hide: () => {
                if (!!drawer) return setDrawerVisible(false);
            },
        },
    };

    React.useEffect(() => {
        let parentBackdrop: Element | undefined;

        if (backdropRef.current) {
            const parentPortalsParent = backdropRef.current.parentElement && backdropRef.current.parentElement.parentElement;

            if (parentPortalsParent && parentPortalsParent.classList.contains('__reakit-portal')) {
                parentBackdrop = Array.from(parentPortalsParent.children).find(x => x.classList.contains('yt-backdrop'));

                if (parentBackdrop) {
                    if (dialog.visible) {
                        parentBackdrop.classList.add('yt-forcibly__hidden');
                    } else {
                        parentBackdrop.classList.remove('yt-forcibly__hidden');
                    }
                }
            }
        }

        return () => {
            if (parentBackdrop) {
                parentBackdrop.classList.remove('yt-forcibly__hidden');
            }
        };
    }, [dialog.visible]);

    const modalWidths: Record<ModalSize, string> = {
        dialog: 'w-128 text-center justify-center',
        xs: 'w-xs',
        sm: 'w-sm',
        md: 'w-md',
        lg: 'w-lg',
    };

    const className = cn(
        'yt-modal__container',
        { 'yt-modal--with-drawer': !!drawer },
        { 'yt-modal--with-extra': !!extra },
        props.className,
        modalWidths[size]
    );

    return (
        <>
            {disclosure && (
                <DialogDisclosure {...dialog} disabled={disclosure.props.disabled} ref={disclosureRef}>
                    {({ type: _, ...disclosureProps }: DialogDisclosureProps) =>
                        React.cloneElement(React.Children.only(disclosure), disclosureProps)
                    }
                </DialogDisclosure>
            )}
            <DialogBackdrop {...dialog} className="yt-backdrop" ref={backdropRef}>
                <Dialog
                    {...otherProps}
                    {...dialog}
                    className={className}
                    ref={modalRef}
                    unstable_finalFocusRef={disclosure ? undefined : defaultDisclosureRef}
                    hideOnClickOutside={hideOnClickOutside}
                    style={{
                        ...otherProps.style,
                        left: `calc(50% + ${position.x}px)`,
                        top: `${position.y}px`,
                    }}>
                    {dialog.visible && (
                        <VisibilityWrapper disclosureRef={disclosureRef}>
                            <ContentWrapper {...wrapperProps}>
                                <div className="yt-modal">
                                    {typeof children === 'function' ? children(renderProps) : children}
                                </div>
                                {drawer && (
                                    <Drawer visible={drawerVisible}>
                                        {typeof drawer === 'function' ? drawer(renderProps) : drawer}
                                    </Drawer>
                                )}
                            </ContentWrapper>
                            {extra && <Extra>{typeof extra === 'function' ? extra(renderProps) : extra}</Extra>}
                            {draggable && <div {...dragHandleProps} aria-label={texts.modal.drag} className="yt-modal__drag" />}
                            {showCloseButton && ( // Keep the close icon as the last element to receive focus
                                <IconButton
                                    appearance="discrete"
                                    aria-label={texts.modal.close}
                                    className="yt-modal__close"
                                    icon="close"
                                    onClick={() => {
                                        closeReasonRef.current = 'close';
                                        dialog.hide();
                                    }}
                                />
                            )}
                        </VisibilityWrapper>
                    )}
                </Dialog>
            </DialogBackdrop>
        </>
    );
};

export type ModalHeaderProps = React.HTMLAttributes<HTMLDivElement> & {
    /**
     * Center the text in header.
     * Default value is `true`
     */
    centered?: boolean;
};

const Header = React.forwardRef(function Header(props: ModalHeaderProps, ref: React.Ref<HTMLDivElement>) {
    const { children, className, centered = true, ...otherProps } = props;
    return (
        <div {...otherProps} ref={ref} className={cn('yt-modal__header', { 'yt-modal__header--centered': centered }, className)}>
            <h2>{children}</h2>
        </div>
    );
});

export type ModalContentProps = React.HTMLAttributes<HTMLDivElement>;

const Content = React.forwardRef(function Content(props: ModalContentProps, ref: React.Ref<HTMLDivElement>) {
    const { children, className, ...otherProps } = props;
    return (
        <div {...otherProps} ref={ref} className={cn('yt-modal__content', className)}>
            {children}
        </div>
    );
});

export type ModalFooterProps = React.HTMLAttributes<HTMLDivElement>;

const Footer = React.forwardRef(function Footer(props: ModalFooterProps, ref: React.Ref<HTMLDivElement>) {
    const { children, className, ...otherProps } = props;
    return (
        <div {...otherProps} ref={ref} className={cn('yt-modal__footer', className)}>
            {children}
        </div>
    );
});

export type ModalDrawerProps = React.HTMLAttributes<HTMLDivElement> & {
    /** Whether the drawer is visible or not */
    visible: boolean;
};

const Drawer = (props: ModalDrawerProps) => {
    const { children, className, visible, ...otherProps } = props;
    const resetScrollRef = React.useCallback(
        node => {
            if (node !== null && visible) {
                node.scrollTop = 0;
            }
        },
        [visible]
    );

    return (
        <motion.div
            className="yt-modal__drawer"
            aria-hidden={!visible}
            variants={{
                visible: {
                    x: '99%',
                    transition: { ease: 'easeOut', duration: 0.2 },
                    display: 'block',
                },
                hidden: {
                    x: '0%',
                    transition: { ease: 'easeOut', duration: 0.2 },
                    transitionEnd: {
                        display: 'none',
                    },
                },
            }}
            animate={visible ? 'visible' : 'hidden'}>
            <div {...otherProps} ref={resetScrollRef}>
                {children}
            </div>
        </motion.div>
    );
};

export type ModalExtraProps = React.HTMLAttributes<HTMLDivElement>;

const Extra = (props: ModalExtraProps) => {
    const { children, className, ...otherProps } = props;

    return (
        <div {...otherProps} className={cn('yt-modal__extra', className)}>
            {children}
        </div>
    );
};

Modal.Header = Header;
Modal.Footer = Footer;
Modal.Content = Content;
